// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { BufferedRemoteLogMessageSender } from "@lib/logging/BufferedRemoteLogMessageSender";
import { IRemoteLogMessageSender } from "@lib/logging/IRemoteLogMessageSender";
import { LogLevel } from "@lib/logging/LogLevel";

/**
 * Provides methods caching log messages in a buffer until a `IRemoteLogMessageSender` is set.
 * Uses a `BufferedRemoteLogMessageSender` as buffer when no `IRemoteLogMessageSender` is available.
 */
export class CachingRemoteLogMessageSender implements IRemoteLogMessageSender
{
  private readonly bufferedRemoteLogMessageSender: BufferedRemoteLogMessageSender;

  private _enabled: boolean = true;
  private _remoteLogMessageSender: Nullable<IRemoteLogMessageSender> = null;
  private skipLogging = false;

  constructor(bufferedRemoteLogMessageSender: BufferedRemoteLogMessageSender)
  {
    this.bufferedRemoteLogMessageSender = bufferedRemoteLogMessageSender;
  }

  get enabled(): boolean
  {
    return this._enabled;
  }

  set enabled(value: boolean)
  {
    this._enabled = value;
    if (value)
      this.flush();
  }

  get remoteLogMessageSender(): Nullable<IRemoteLogMessageSender>
  {
    return this._remoteLogMessageSender;
  }

  set remoteLogMessageSender(value: Nullable<IRemoteLogMessageSender>)
  {
    this._remoteLogMessageSender = value;
    if (value)
      this.flush();
  }

  sendLogMessage(logLevel: LogLevel, format: string, args: any[]): void
  {
    if (this.skipLogging)
      return;

    this.skipLogging = true;
    try
    {
      if (this._enabled && this._remoteLogMessageSender)
        this._remoteLogMessageSender.sendLogMessage(logLevel, format, args);
      else
        this.bufferedRemoteLogMessageSender.sendLogMessage(logLevel, format, args);
    }
    finally
    {
      this.skipLogging = false;
    }
  }

  private flush(): void
  {
    if (!this._remoteLogMessageSender)
      return;

    this.bufferedRemoteLogMessageSender.flush(this._remoteLogMessageSender);
  }
}